import React from 'react';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { DiagnosticError } from '@/components/DiagnosticError/DiagnosticError';
import { SectionSummary } from '@/components/SectionSummary/SectionSummary';
import { DiagnosticErrorItem } from '@/components/DiagnosticError/DiagnosticError.types';
import { CommunicationStatusCard } from '@/components/CommunicationStatusCard/CommunicationStatusCard';
import { CardSectionWide } from '@/components/CardSectionWideRate/CardSectionWide';
import { Verdict } from '@/helpers/ratingState';
import { PhotoGallery } from '@/components/PhotoGallery/PhotoGallery';
import type { PhotoGalleryItem } from '@/components/PhotoGallery/PhotoGallery.types';

interface RaportDiagnosticComputerProps {
  /** Czy wykryto błędy diagnostyczne */
  hasErrors: boolean;
  /** Status odczytu diagnostyki */
  readStatus: string | null;
  /** Lista błędów diagnostycznych */
  errors: DiagnosticErrorItem[];
  /** Podsumowanie rzeczoznawcy dotyczące diagnostyki komputerowej */
  expertSummary: string | null;
  /** Czy udało się nawiązać komunikację z pojazdem */
  isCommunicationSuccessful: boolean;
  /** Zdjęcia z diagnostyki */
  galleryPhotos?: PhotoGalleryItem[];
}

export default function RaportDiagnosticComputer({
  hasErrors,
  errors,
  expertSummary,
  isCommunicationSuccessful,
  galleryPhotos,
}: RaportDiagnosticComputerProps) {
  if (isCommunicationSuccessful && !hasErrors) {
    return (
      <>
        <div className="pt-10 w-full" id="podsumowanie-diagnostyki-komputerowej">
          <CardSectionWide
            verdict={Verdict.POSITIVE}
            badgeCustomText="Pozytywna"
            title="Czy udało się skomunikować z pojazdem"
            description="Wygląda na to, że nasza ekspertyza nie wykazała żadnych nieprawidłowości podczas przeprowadzania diagnostyki komputerowej."
          />

          {expertSummary && (
            <div className="mt-20">
              <SectionSummary
                iconName="monitor-screen"
                title="Podsumowanie diagnostyki komputerowej"
                content={expertSummary}
              />
            </div>
          )}
        </div>

        {galleryPhotos && galleryPhotos.length > 0 && (
          <div id="galeria-diagnostyki" className="mt-12">
            <PhotoGallery
              photos={galleryPhotos}
              title="Galeria diagnostyki komputerowej"
              iconName="monitor-screen"
              collapsible={true}
              defaultExpanded={false}
              previewCount={4}
              galleryVariant="default"
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="mt-[58px] w-full" id="podsumowanie-diagnostyki-komputerowej">
        <SectionSubtitle
          title="Podsumowanie diagnostyki komputerowej"
          description="Status komunikacji oraz wykryte błędy."
        />

        <div className="flex gap-11 items-start mt-11">
          <CommunicationStatusCard
            question="Czy udało się skomunikować z pojazdem"
            isPositive={isCommunicationSuccessful}
          />
        </div>

        {hasErrors && errors.length > 0 && isCommunicationSuccessful && (
          <div className="mt-20">
            <SectionSubtitle
              title="Wykryte błędy diagnostyczne"
              description="Lista błędów wykrytych podczas skanowania komputerem diagnostycznym."
            />

            <div className="mt-10">
              <DiagnosticError errors={errors} />
            </div>
          </div>
        )}

        {expertSummary && (
          <div className="mt-20">
            <SectionSummary
              iconName="monitor-screen"
              title="Podsumowanie diagnostyki komputerowej"
              content={expertSummary}
            />
          </div>
        )}
      </div>

      {galleryPhotos && galleryPhotos.length > 0 && (
        <div id="galeria-diagnostyki" className="mt-12">
          <PhotoGallery
            photos={galleryPhotos}
            title="Galeria diagnostyki komputerowej"
            iconName="monitor-screen"
            collapsible={true}
            defaultExpanded={false}
            previewCount={4}
            galleryVariant="default"
          />
        </div>
      )}
    </>
  );
}
